import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Container,
  Stack,
  alpha,
  useTheme,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  useMediaQuery,
  IconButton,
  Tooltip,
  TextField,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import api from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { getPublicApiBase, publicApiUrl } from '../../utils/publicApi';
import { PublicJobDetailBody } from './PublicJobDetailView';
import { usePublicPortalAppearance } from '../../context/PublicPortalAppearanceContext';
import {
  getStoredSplitRatio,
  setStoredSplitRatio,
  setStoredSplitListHidden
} from '../../utils/publicPortalPreferences';

const scrollToHash = (hash) => {
  if (!hash || hash.length < 2) return;
  const id = hash.replace('#', '');
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

function matchRemoteOption(/** @type {string} */ filterVal, /** @type {string|undefined} */ remoteOption) {
  const ro = (remoteOption || '').toLowerCase();
  if (filterVal === 'any') return true;
  if (filterVal === 'remote') return ro.includes('remote');
  if (filterVal === 'hybrid') return ro.includes('hybrid');
  if (filterVal === 'onsite') {
    return ro.includes('on-site') || ro.includes('onsite') || ro.includes('office') || ro.includes('in office');
  }
  return true;
}

/** @param {{ publishedCourses?: Array<{ subCategoryId?: string, subCategoryName?: string }> }} cat */
function groupCoursesByLearningSpot(publishedCourses) {
  const map = new Map();
  for (const c of publishedCourses || []) {
    const key = c.subCategoryId || '_';
    if (!map.has(key)) {
      map.set(key, {
        id: c.subCategoryId,
        name: c.subCategoryName || 'Learning area',
        courses: []
      });
    }
    map.get(key).courses.push(c);
  }
  return [...map.values()];
}

function formatDeliveryType(/** @type {string|undefined} */ v) {
  if (!v) return '—';
  const s = String(v).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function PublicTrainingCourseDetailBlock({
  course,
  summary,
  feedbackEligibility,
  feedbackRating,
  onFeedbackRatingChange,
  feedbackThumb,
  onFeedbackThumbChange,
  feedbackComment,
  onFeedbackCommentChange,
  onSubmitFeedback,
  feedbackSubmitting,
  feedbackMessage
}) {
  if (!course) return null;
  const el = feedbackEligibility;
  const showForm = el?.authenticated && el?.completed && el?.canSubmit;
  const my = el?.myFeedback;
  return (
    <>
      <Typography variant="overline" color="text.secondary">
        {course.category?.name || 'Program'}
        {course.learningSpot?.name ? ` · ${course.learningSpot.name}` : ''}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        {course.name}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        <Chip size="small" label={`Delivery: ${formatDeliveryType(course.delivery_type)}`} />
        {course.schedule_model && <Chip size="small" variant="outlined" label={String(course.schedule_model)} />}
        {course.course_code && <Chip size="small" variant="outlined" label={course.course_code} />}
      </Stack>
      {(course.start_date || course.end_date || course.weekdays || course.session_start_time) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {[
            course.start_date && course.end_date
              ? `${course.start_date} → ${course.end_date}`
              : course.start_date || course.end_date || null,
            course.weekdays,
            course.session_start_time && course.session_end_time
              ? `${course.session_start_time}–${course.session_end_time}`
              : course.session_start_time || course.session_end_time
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      )}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Course feedback
      </Typography>
      {summary && summary.count > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Approved average:{' '}
          <strong>{summary.avgRating != null ? `${summary.avgRating} / 5` : '—'}</strong> ({summary.count}{' '}
          {summary.count === 1 ? 'review' : 'reviews'})
        </Typography>
      )}
      {Array.isArray(summary?.items) && summary.items.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {summary.items.slice(0, 8).map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 1.25 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Rating value={Number(item.rating) || 0} readOnly size="small" />
                {item.thumb === 'up' && <ThumbUpAltIcon fontSize="small" color="success" />}
                {item.thumb === 'down' && <ThumbDownAltIcon fontSize="small" color="error" />}
                <Typography variant="caption" color="text.secondary">
                  {item.submitter_name} · Approved
                </Typography>
              </Stack>
              {item.comment && (
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  {item.comment}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      )}
      {el?.loading ? (
        <Box sx={{ py: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : el?.error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load feedback eligibility. Try signing in again.
        </Alert>
      ) : !el?.authenticated ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sign in, enroll, and complete this course to leave a 5★ review (optional thumbs + comment). Instructors approve
          feedback before it appears under the course.
        </Alert>
      ) : !el?.completed ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Complete all required outline items for this course, then return here to submit feedback.
        </Alert>
      ) : my?.review_status === 'pending' || el?.reason === 'pending_review' ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Status: <strong>Submitted</strong> — waiting for instructor approval.
        </Alert>
      ) : my?.review_status === 'accepted' || el?.reason === 'already_accepted' ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Status: <strong>Approved</strong> — your feedback is published under this course.
        </Alert>
      ) : null}
      {showForm && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Your rating (1–5 stars)
          </Typography>
          <Rating
            value={Number(feedbackRating) || 0}
            onChange={(e, v) => onFeedbackRatingChange(v || 1)}
            size="large"
            sx={{ mb: 1.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Thumbs (optional)
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={feedbackThumb || null}
            onChange={(e, v) => onFeedbackThumbChange(v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="up" aria-label="Thumbs up">
              {feedbackThumb === 'up' ? <ThumbUpAltIcon /> : <ThumbUpAltOutlinedIcon />}
            </ToggleButton>
            <ToggleButton value="down" aria-label="Thumbs down">
              {feedbackThumb === 'down' ? <ThumbDownAltIcon /> : <ThumbDownAltOutlinedIcon />}
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="Comments (optional)"
            placeholder="What worked well? What could improve?"
            value={feedbackComment}
            onChange={(e) => onFeedbackCommentChange(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mb: 2 }}
          />
          {feedbackMessage && (
            <Alert severity={feedbackMessage.type === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>
              {feedbackMessage.text}
            </Alert>
          )}
          <Button variant="outlined" onClick={onSubmitFeedback} disabled={feedbackSubmitting} sx={{ mb: 2 }}>
            {feedbackSubmitting ? 'Submitting…' : 'Submit feedback'}
          </Button>
        </>
      )}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Enroll
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create an account or sign in to enroll in this course after you have reviewed the details above.
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button component={RouterLink} to="/signup" variant="contained">
          Sign up to enroll
        </Button>
        <Button component={RouterLink} to="/login">
          Log in
        </Button>
      </Stack>
    </>
  );
}

function PublicExamDetailBlock({ exam }) {
  if (!exam) return null;
  return (
    <>
      <Typography variant="overline" color="text.secondary">
        {exam.category?.name || 'Exam'}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        {exam.name}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {exam.time_limit_minutes != null && <Chip size="small" label={`${exam.time_limit_minutes} min`} />}
        {exam.pass_score_percent != null && (
          <Chip size="small" variant="outlined" label={`Pass ${exam.pass_score_percent}%`} />
        )}
      </Stack>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
        {exam.description || '—'}
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Take this exam
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sign in to your workspace and open Trainings → Exams to start this assessment.
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button component={RouterLink} to="/signup" variant="contained">
          Sign up
        </Button>
        <Button component={RouterLink} to="/login">
          Log in
        </Button>
      </Stack>
    </>
  );
}

function SplitMorePreviewPanel({ splitTab }) {
  if (splitTab === 'more') {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          More coming
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Additional public resources will appear here as your organization enables them.
        </Typography>
      </Box>
    );
  }

  if (splitTab === 'jobs') {
    return null;
  }

  return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Typography color="text.secondary">Select an item in the list on the left to preview it here.</Typography>
    </Box>
  );
}

const PublicPortalPage = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const {
    layoutMode,
    contentPanelTheme,
    publicBranding,
    splitBrowsePanelTheme,
    splitPreviewPanelTheme
  } = usePublicPortalAppearance();

  const panelCardSx = useMemo(
    () => ({
      bgcolor: contentPanelTheme.panelBg,
      borderColor: contentPanelTheme.panelBorder,
      boxShadow: contentPanelTheme.panelShadow,
      border: '1px solid',
      borderStyle: 'solid',
      color: contentPanelTheme.textPrimary
    }),
    [contentPanelTheme]
  );
  const panelTextScopeSx = useMemo(
    () => ({
      color: contentPanelTheme.textPrimary,
      '& .MuiTypography-colorTextSecondary': { color: `${contentPanelTheme.textSecondary} !important` }
    }),
    [contentPanelTheme]
  );

  const splitBrowsePanelSurfaceSx = useMemo(
    () => ({
      bgcolor: splitBrowsePanelTheme.panelBg,
      borderColor: splitBrowsePanelTheme.panelBorder,
      boxShadow: splitBrowsePanelTheme.panelShadow,
      // Keep the left browse column visually distinct from the page background
      outline: `1px solid ${splitBrowsePanelTheme.panelBorder || 'rgba(148,163,184,0.45)'}`,
      outlineOffset: -1,
      zIndex: 1,
    }),
    [splitBrowsePanelTheme]
  );
  const splitBrowsePanelTextScopeSx = useMemo(
    () => ({
      color: splitBrowsePanelTheme.textPrimary,
      '& .MuiTypography-colorTextSecondary': { color: `${splitBrowsePanelTheme.textSecondary} !important` }
    }),
    [splitBrowsePanelTheme]
  );

  const splitPreviewPanelCardSx = useMemo(
    () => ({
      bgcolor: splitPreviewPanelTheme.panelBg,
      borderColor: splitPreviewPanelTheme.panelBorder,
      boxShadow: splitPreviewPanelTheme.panelShadow,
      border: '1px solid',
      borderStyle: 'solid',
      color: splitPreviewPanelTheme.textPrimary
    }),
    [splitPreviewPanelTheme]
  );
  const splitPreviewPanelTextScopeSx = useMemo(
    () => ({
      color: splitPreviewPanelTheme.textPrimary,
      '& .MuiTypography-colorTextSecondary': { color: `${splitPreviewPanelTheme.textSecondary} !important` }
    }),
    [splitPreviewPanelTheme]
  );

  const [loading, setLoading] = useState(true);
  const [vis, setVis] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [training, setTraining] = useState([]);
  const [exams, setExams] = useState([]);
  const [publicBenches, setPublicBenches] = useState([]);
  const [loadError, setLoadError] = useState('');

  const [splitTab, setSplitTab] = useState('jobs');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedTrainingId, setSelectedTrainingId] = useState(null);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const splitDefaultsAppliedRef = useRef(false);
  const splitContainerRef = useRef(null);
  const splitLeftPctRef = useRef(getStoredSplitRatio() * 100);
  const [splitLeftPct, setSplitLeftPct] = useState(() => getStoredSplitRatio() * 100);
  // Always show browse list when opening Query page (persisted "hidden" made the left pane look missing).
  const [splitListHidden, setSplitListHidden] = useState(false);
  useEffect(() => {
    setStoredSplitListHidden(false);
  }, []);
  splitLeftPctRef.current = splitLeftPct;

  const [jobFilters, setJobFilters] = useState({
    search: '',
    location: '',
    client: '',
    remote: 'any'
  });
  /** 'browse' = filters + list on the right; 'detail' = full job in frame */
  const [jobSplitView, setJobSplitView] = useState('browse');
  const [detailJob, setDetailJob] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [trainingViewMode, setTrainingViewMode] = useState(/** @type {'category' | 'course'} */ ('category'));
  const [selectedTrainingCourseId, setSelectedTrainingCourseId] = useState(null);
  const [detailTrainingCourse, setDetailTrainingCourse] = useState(null);
  const [detailTrainingLoading, setDetailTrainingLoading] = useState(false);
  const [trainingFeedbackSummary, setTrainingFeedbackSummary] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackThumb, setFeedbackThumb] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  const [detailExam, setDetailExam] = useState(null);
  const [detailExamLoading, setDetailExamLoading] = useState(false);

  const [trainingDialogCourseId, setTrainingDialogCourseId] = useState(null);
  const [dialogTrainingCourse, setDialogTrainingCourse] = useState(null);
  const [dialogTrainingLoading, setDialogTrainingLoading] = useState(false);
  const [dialogTrainingFeedbackSummary, setDialogTrainingFeedbackSummary] = useState(null);
  const [dialogFeedbackRating, setDialogFeedbackRating] = useState(5);
  const [dialogFeedbackThumb, setDialogFeedbackThumb] = useState(null);
  const [dialogFeedbackComment, setDialogFeedbackComment] = useState('');
  const [dialogFeedbackSubmitting, setDialogFeedbackSubmitting] = useState(false);
  const [dialogFeedbackMessage, setDialogFeedbackMessage] = useState(null);

  const [examDialogId, setExamDialogId] = useState(null);
  const [dialogExam, setDialogExam] = useState(null);
  const [dialogExamLoading, setDialogExamLoading] = useState(false);

  const [trainingBrowseScope, setTrainingBrowseScope] = useState(/** @type {'all' | 'category'} */ ('category'));
  const [trainingFilters, setTrainingFilters] = useState({
    search: '',
    categoryId: '',
    delivery: 'any',
    learningSpotId: ''
  });
  const [examFilters, setExamFilters] = useState({
    search: '',
    timeLimit: 'any',
    passAtLeast: ''
  });
  const [feedbackEligibility, setFeedbackEligibility] = useState(null);
  const [dialogFeedbackEligibility, setDialogFeedbackEligibility] = useState(null);

  const portalBase =
    location.pathname.startsWith('/query') || location.pathname.startsWith('/public') ? '/query' : '/query';
  const sectionHref = (id) => `${portalBase}#${id}`;
  const jobsSectionPath = portalBase;

  const filteredJobs = useMemo(() => {
    if (!jobs.length) return [];
    return jobs.filter((j) => {
      const q = jobFilters.search.trim().toLowerCase();
      if (q) {
        const blob = [
          j.title,
          j.job_code,
          j.description_external,
          j.skills_required,
          j.skills_preferred,
          j.client?.name,
          j.client?.company_name,
          j.location,
          j.city,
          j.state,
          j.country,
          j.employment_type
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!blob.includes(q)) return false;
      }
      const loc = jobFilters.location.trim().toLowerCase();
      if (loc) {
        const blob = [j.location, j.city, j.state, j.country].filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(loc)) return false;
      }
      const cl = jobFilters.client.trim().toLowerCase();
      if (cl) {
        const name = (j.client?.name || j.client?.company_name || '').toLowerCase();
        if (!name.includes(cl)) return false;
      }
      if (!matchRemoteOption(jobFilters.remote, j.remote_option)) return false;
      return true;
    });
  }, [jobs, jobFilters]);

  const trainingRows = useMemo(
    () => training.filter((c) => (c.publishedCourseCount || 0) > 0),
    [training]
  );

  const allTrainingCoursesFlat = useMemo(() => {
    const out = [];
    for (const cat of trainingRows) {
      for (const c of cat.publishedCourses || []) {
        out.push({
          ...c,
          categoryId: cat.id,
          categoryName: cat.name
        });
      }
    }
    return out;
  }, [trainingRows]);

  const filteredAllTrainingCourses = useMemo(() => {
    let list = allTrainingCoursesFlat;
    const q = trainingFilters.search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        const blob = [c.name, c.categoryName, c.subCategoryName, c.delivery_type].filter(Boolean).join(' ').toLowerCase();
        return blob.includes(q);
      });
    }
    if (trainingFilters.categoryId) {
      list = list.filter((c) => c.categoryId === trainingFilters.categoryId);
    }
    if (trainingFilters.delivery !== 'any') {
      list = list.filter((c) => c.delivery_type === trainingFilters.delivery);
    }
    if (trainingFilters.learningSpotId) {
      list = list.filter((c) => c.subCategoryId === trainingFilters.learningSpotId);
    }
    return list;
  }, [allTrainingCoursesFlat, trainingFilters]);

  const learningSpotOptions = useMemo(() => {
    const m = new Map();
    for (const c of allTrainingCoursesFlat) {
      if (c.subCategoryId && c.subCategoryName) {
        m.set(c.subCategoryId, c.subCategoryName);
      }
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [allTrainingCoursesFlat]);

  const filteredExams = useMemo(() => {
    if (!exams.length) return [];
    let list = exams;
    const q = examFilters.search.trim().toLowerCase();
    if (q) {
      list = list.filter((ex) => {
        const blob = [ex.name, ex.description, ex.category?.name].filter(Boolean).join(' ').toLowerCase();
        return blob.includes(q);
      });
    }
    if (examFilters.timeLimit === 'short') {
      list = list.filter((ex) => ex.time_limit_minutes != null && ex.time_limit_minutes <= 45);
    } else if (examFilters.timeLimit === 'long') {
      list = list.filter((ex) => ex.time_limit_minutes == null || ex.time_limit_minutes > 45);
    }
    const minPass = parseInt(examFilters.passAtLeast, 10);
    if (!Number.isNaN(minPass) && examFilters.passAtLeast !== '') {
      list = list.filter((ex) => ex.pass_score_percent != null && ex.pass_score_percent >= minPass);
    }
    return list;
  }, [exams, examFilters]);

  useEffect(() => {
    const base = getPublicApiBase();
    let cancelled = false;
    (async () => {
      try {
        setLoadError('');
        const [vRes, jRes, tRes, eRes] = await Promise.all([
          axios.get(`${base}/visibility`),
          axios.get(`${base}/jobs`),
          axios.get(`${base}/training/catalog`),
          axios.get(`${base}/exams`)
        ]);
        if (cancelled) return;
        const v = vRes.data?.data || {};
        setVis({ training: v.training, jobs: v.jobs, exams: v.exams, benches: v.benches });
        setJobs(jRes.data?.data?.jobs || []);
        setTraining(tRes.data?.data?.categories || []);
        setExams(eRes.data?.data?.exams || []);
        if (v.benches === 'public') {
          try {
            const benchRes = await axios.get(`${base}/bench/catalog`);
            setPublicBenches(benchRes.data?.data?.benches || []);
          } catch {
            setPublicBenches([]);
          }
        } else {
          setPublicBenches([]);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setVis({ training: 'private', jobs: 'private', exams: 'private', benches: 'private' });
          setLoadError(
            e.response?.data?.message ||
              (e.code === 'ERR_NETWORK' ? 'Cannot reach the API. Check that the backend is running and the dev proxy targets it.' : '') ||
              'Could not load public content.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    const h = location.hash;
    if (!h || h.length < 2) return;
    const t = window.setTimeout(() => scrollToHash(h), 140);
    return () => clearTimeout(t);
  }, [loading, location.hash]);

  const jobsPublic = vis?.jobs === 'public';
  const trainingPublic = vis?.training === 'public';
  const examsPublic = vis?.exams === 'public';
  const benchesPublic = vis?.benches === 'public';
  const anyPublic = jobsPublic || trainingPublic || examsPublic || benchesPublic;

  useEffect(() => {
    if (layoutMode === 'regular') {
      splitDefaultsAppliedRef.current = false;
    }
  }, [layoutMode]);

  useEffect(() => {
    if (loading || layoutMode !== 'split') return;
    const hash = (location.hash || '').replace('#', '');

    const mTrainCat = hash.match(/^portal-training-cat-(.+)$/);
    if (mTrainCat && trainingPublic) {
      if (trainingRows.length === 0) return;
      const rawId = mTrainCat[1];
      const cat = trainingRows.find((c) => String(c.id) === String(rawId));
      setSplitTab('training');
      setSelectedTrainingId(cat ? cat.id : rawId);
      setTrainingBrowseScope('category');
      setTrainingViewMode('category');
      splitDefaultsAppliedRef.current = true;
      return;
    }

    if (hash === 'portal-training' && trainingPublic) {
      setSplitTab('training');
      splitDefaultsAppliedRef.current = true;
      return;
    }
    if (hash === 'portal-jobs' && jobsPublic) {
      setSplitTab('jobs');
      splitDefaultsAppliedRef.current = true;
      return;
    }
    if (hash === 'portal-exams' && examsPublic) {
      setSplitTab('exams');
      splitDefaultsAppliedRef.current = true;
      return;
    }

    if (splitDefaultsAppliedRef.current) return;
    splitDefaultsAppliedRef.current = true;
    if (jobsPublic && jobs.length) {
      setSplitTab('jobs');
      setSelectedJobId(jobs[0].id);
    } else if (trainingPublic && trainingRows.length) {
      setSplitTab('training');
      setSelectedTrainingId(trainingRows[0].id);
    } else if (examsPublic && exams.length) {
      setSplitTab('exams');
      setSelectedExamId(exams[0].id);
    } else {
      setSplitTab('more');
    }
  }, [
    loading,
    layoutMode,
    location.hash,
    jobsPublic,
    trainingPublic,
    examsPublic,
    jobs,
    trainingRows,
    exams
  ]);

  const openJobDetailInFrame = useCallback((id) => {
    setSelectedJobId(id);
    setJobSplitView('detail');
  }, []);

  useEffect(() => {
    if (splitTab !== 'jobs') {
      setJobSplitView('browse');
      setDetailJob(null);
    }
  }, [splitTab]);

  useEffect(() => {
    if (layoutMode !== 'split' || splitTab !== 'jobs' || jobSplitView !== 'detail' || !selectedJobId) {
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    axios
      .get(publicApiUrl(`/jobs/${selectedJobId}`))
      .then((r) => {
        if (!cancelled) setDetailJob(r.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setDetailJob(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [layoutMode, splitTab, jobSplitView, selectedJobId]);

  useEffect(() => {
    if (splitTab !== 'training') {
      setTrainingViewMode('category');
      setSelectedTrainingCourseId(null);
      setDetailTrainingCourse(null);
      setTrainingFeedbackSummary(null);
      setFeedbackMessage(null);
      setTrainingBrowseScope('category');
      setFeedbackEligibility(null);
    }
  }, [splitTab]);

  useEffect(() => {
    setTrainingViewMode('category');
    setSelectedTrainingCourseId(null);
    setDetailTrainingCourse(null);
    setTrainingFeedbackSummary(null);
    setFeedbackMessage(null);
  }, [selectedTrainingId]);

  useEffect(() => {
    if (layoutMode !== 'split' || splitTab !== 'training' || trainingViewMode !== 'course' || !selectedTrainingCourseId) {
      return;
    }
    let cancelled = false;
    setDetailTrainingLoading(true);
    setFeedbackMessage(null);
    setFeedbackRating(5);
    setFeedbackThumb(null);
    setFeedbackComment('');
    Promise.all([
      axios.get(publicApiUrl(`/training/courses/${selectedTrainingCourseId}`)),
      axios.get(publicApiUrl(`/training/courses/${selectedTrainingCourseId}/feedback/summary`))
    ])
      .then(([cRes, sRes]) => {
        if (cancelled) return;
        setDetailTrainingCourse(cRes.data?.data || null);
        setTrainingFeedbackSummary(sRes.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) {
          setDetailTrainingCourse(null);
          setTrainingFeedbackSummary(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDetailTrainingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [layoutMode, splitTab, trainingViewMode, selectedTrainingCourseId]);

  useEffect(() => {
    if (layoutMode !== 'split' || splitTab !== 'training' || trainingViewMode !== 'course' || !selectedTrainingCourseId) {
      setFeedbackEligibility(null);
      return;
    }
    const token = getAuthAccessToken();
    if (!token) {
      setFeedbackEligibility({ loading: false, authenticated: false });
      return;
    }
    let cancelled = false;
    setFeedbackEligibility({ loading: true, authenticated: true });
    api
      .get(`/training/catalog/courses/${selectedTrainingCourseId}/public-feedback/eligibility`)
      .then((r) => {
        if (!cancelled) setFeedbackEligibility({ loading: false, authenticated: true, ...r.data?.data });
      })
      .catch(() => {
        if (!cancelled) setFeedbackEligibility({ loading: false, authenticated: true, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [layoutMode, splitTab, trainingViewMode, selectedTrainingCourseId]);

  useEffect(() => {
    if (layoutMode !== 'split' || splitTab !== 'exams' || !selectedExamId) {
      return;
    }
    let cancelled = false;
    setDetailExamLoading(true);
    axios
      .get(publicApiUrl(`/exams/${selectedExamId}`))
      .then((r) => {
        if (!cancelled) setDetailExam(r.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setDetailExam(null);
      })
      .finally(() => {
        if (!cancelled) setDetailExamLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [layoutMode, splitTab, selectedExamId]);

  useEffect(() => {
    if (!trainingDialogCourseId) {
      setDialogTrainingCourse(null);
      setDialogTrainingFeedbackSummary(null);
      setDialogFeedbackMessage(null);
      return;
    }
    let cancelled = false;
    setDialogTrainingLoading(true);
    setDialogFeedbackRating(5);
    setDialogFeedbackThumb(null);
    setDialogFeedbackComment('');
    setDialogFeedbackMessage(null);
    Promise.all([
      axios.get(publicApiUrl(`/training/courses/${trainingDialogCourseId}`)),
      axios.get(publicApiUrl(`/training/courses/${trainingDialogCourseId}/feedback/summary`))
    ])
      .then(([cRes, sRes]) => {
        if (cancelled) return;
        setDialogTrainingCourse(cRes.data?.data || null);
        setDialogTrainingFeedbackSummary(sRes.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) {
          setDialogTrainingCourse(null);
          setDialogTrainingFeedbackSummary(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDialogTrainingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trainingDialogCourseId]);

  useEffect(() => {
    if (!trainingDialogCourseId) {
      setDialogFeedbackEligibility(null);
      return;
    }
    const token = getAuthAccessToken();
    if (!token) {
      setDialogFeedbackEligibility({ loading: false, authenticated: false });
      return;
    }
    let cancelled = false;
    setDialogFeedbackEligibility({ loading: true, authenticated: true });
    api
      .get(`/training/catalog/courses/${trainingDialogCourseId}/public-feedback/eligibility`)
      .then((r) => {
        if (!cancelled) setDialogFeedbackEligibility({ loading: false, authenticated: true, ...r.data?.data });
      })
      .catch(() => {
        if (!cancelled) setDialogFeedbackEligibility({ loading: false, authenticated: true, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [trainingDialogCourseId]);

  useEffect(() => {
    if (!examDialogId) {
      setDialogExam(null);
      return;
    }
    let cancelled = false;
    setDialogExamLoading(true);
    axios
      .get(publicApiUrl(`/exams/${examDialogId}`))
      .then((r) => {
        if (!cancelled) setDialogExam(r.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setDialogExam(null);
      })
      .finally(() => {
        if (!cancelled) setDialogExamLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [examDialogId]);

  useEffect(() => {
    if (splitTab !== 'jobs') return;
    if (filteredJobs.length === 0) {
      setSelectedJobId(null);
      setJobSplitView('browse');
      setDetailJob(null);
      return;
    }
    if (!selectedJobId) return;
    if (!filteredJobs.some((j) => j.id === selectedJobId)) {
      setSelectedJobId(filteredJobs[0].id);
      setJobSplitView('browse');
      setDetailJob(null);
    }
  }, [filteredJobs, splitTab, selectedJobId]);

  useEffect(() => {
    if (splitTab !== 'exams') return;
    if (filteredExams.length === 0) {
      setSelectedExamId(null);
      return;
    }
    if (!selectedExamId || !filteredExams.some((e) => e.id === selectedExamId)) {
      setSelectedExamId(filteredExams[0].id);
    }
  }, [filteredExams, splitTab, selectedExamId]);

  const onSplitTabChange = useCallback(
    (tab) => {
      setSplitTab(tab);
      if (tab === 'jobs' && jobsPublic && jobs.length) {
        setJobSplitView('browse');
        setDetailJob(null);
        setSelectedJobId(jobs[0].id);
        setSelectedTrainingId(null);
        setSelectedExamId(null);
      } else if (tab === 'training' && trainingPublic && trainingRows.length) {
        setTrainingBrowseScope('category');
        setSelectedTrainingId(trainingRows[0].id);
        setSelectedJobId(null);
        setSelectedExamId(null);
      } else if (tab === 'exams' && examsPublic && exams.length) {
        setExamFilters({ search: '', timeLimit: 'any', passAtLeast: '' });
        setSelectedExamId(exams[0].id);
        setSelectedJobId(null);
        setSelectedTrainingId(null);
      } else {
        setSelectedJobId(null);
        setSelectedTrainingId(null);
        setSelectedExamId(null);
      }
    },
    [jobsPublic, trainingPublic, examsPublic, jobs, trainingRows, exams]
  );

  const submitSplitTrainingFeedback = useCallback(async () => {
    if (!selectedTrainingCourseId) return;
    setFeedbackSubmitting(true);
    setFeedbackMessage(null);
    try {
      const res = await api.post(`/training/catalog/courses/${selectedTrainingCourseId}/public-feedback`, {
        rating: feedbackRating,
        thumb: feedbackThumb || undefined,
        comment: feedbackComment.trim() || undefined
      });
      setFeedbackMessage({
        type: 'success',
        text:
          res.data?.message ||
          'Your feedback has been submitted for instructor review (status: Submitted).'
      });
      setFeedbackMessage({
        type: 'success',
        text:
          res.data?.message ||
          'Your feedback has been sent to the course owner for review.'
      });
      setFeedbackComment('');
      const [sRes, elRes] = await Promise.all([
        axios.get(publicApiUrl(`/training/courses/${selectedTrainingCourseId}/feedback/summary`)),
        api.get(`/training/catalog/courses/${selectedTrainingCourseId}/public-feedback/eligibility`)
      ]);
      setTrainingFeedbackSummary(sRes.data?.data || null);
      setFeedbackEligibility({ loading: false, authenticated: true, ...elRes.data?.data });
    } catch (e) {
      setFeedbackMessage({
        type: 'error',
        text: e.response?.data?.message || 'Could not save feedback.'
      });
    } finally {
      setFeedbackSubmitting(false);
    }
  }, [selectedTrainingCourseId, feedbackRating, feedbackThumb, feedbackComment]);

  const submitDialogTrainingFeedback = useCallback(async () => {
    if (!trainingDialogCourseId) return;
    setDialogFeedbackSubmitting(true);
    setDialogFeedbackMessage(null);
    try {
      const res = await api.post(`/training/catalog/courses/${trainingDialogCourseId}/public-feedback`, {
        rating: dialogFeedbackRating,
        thumb: dialogFeedbackThumb || undefined,
        comment: dialogFeedbackComment.trim() || undefined
      });
      setDialogFeedbackMessage({
        type: 'success',
        text:
          res.data?.message ||
          'Your feedback has been submitted for instructor review (status: Submitted).'
      });
      setDialogFeedbackComment('');
      setDialogFeedbackThumb(null);
      const [sRes, elRes] = await Promise.all([
        axios.get(publicApiUrl(`/training/courses/${trainingDialogCourseId}/feedback/summary`)),
        api.get(`/training/catalog/courses/${trainingDialogCourseId}/public-feedback/eligibility`)
      ]);
      setDialogTrainingFeedbackSummary(sRes.data?.data || null);
      setDialogFeedbackEligibility({ loading: false, authenticated: true, ...elRes.data?.data });
    } catch (e) {
      setDialogFeedbackMessage({
        type: 'error',
        text: e.response?.data?.message || 'Could not save feedback.'
      });
    } finally {
      setDialogFeedbackSubmitting(false);
    }
  }, [trainingDialogCourseId, dialogFeedbackRating, dialogFeedbackThumb, dialogFeedbackComment]);

  const handleSplitDividerPointerDown = useCallback((e) => {
    if (splitListHidden) return;
    e.preventDefault();
    const el = splitContainerRef.current;
    if (!el) return;

    const pointerMove = (ev) => {
      if (ev.cancelable && ev.type === 'touchmove') ev.preventDefault();
      const rect = el.getBoundingClientRect();
      const w = rect.width;
      if (w < 40) return;
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const x = clientX - rect.left;
      const pct = (x / w) * 100;
      const clamped = Math.min(48, Math.max(18, pct));
      splitLeftPctRef.current = clamped;
      setSplitLeftPct(clamped);
    };

    const pointerUp = () => {
      window.removeEventListener('mousemove', pointerMove);
      window.removeEventListener('mouseup', pointerUp);
      window.removeEventListener('touchmove', pointerMove);
      window.removeEventListener('touchend', pointerUp);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      setStoredSplitRatio(splitLeftPctRef.current / 100);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', pointerMove);
    window.addEventListener('mouseup', pointerUp);
    window.addEventListener('touchmove', pointerMove, { passive: false });
    window.addEventListener('touchend', pointerUp);
  }, [splitListHidden]);

  const hideSplitList = useCallback(() => {
    setSplitListHidden(true);
    setStoredSplitListHidden(true);
  }, []);

  const showSplitList = useCallback(() => {
    setSplitListHidden(false);
    setStoredSplitListHidden(false);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const alertsBlock = (
    <>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {!anyPublic && !loadError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Public listings are not enabled yet. Please log in to access your workspace, or ask an administrator to enable
          visibility under Administration → Visibility setup (set Training / Jobs / Exams to Public as needed).
        </Alert>
      )}
      {!loadError && jobsPublic && jobs.length === 0 && layoutMode === 'regular' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No jobs match the public list yet. Jobs must be <strong>Active</strong>, set to <strong>public listing</strong>{' '}
          on the job, and the tenant must have <strong>Jobs</strong> set to <strong>Public</strong> under Visibility setup.
        </Alert>
      )}
    </>
  );

  const regularSections = (
    <Box sx={panelTextScopeSx}>
      <Box id="portal-jobs" sx={{ scrollMarginTop: 88, mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <WorkIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography
            component={RouterLink}
            to={`${jobsSectionPath}#portal-jobs`}
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Open positions
          </Typography>
        </Stack>
        {!jobsPublic ? (
          <Typography color="text.secondary">Job listings are available only to signed-in users.</Typography>
        ) : jobs.length === 0 ? (
          <Card variant="outlined" sx={{ borderRadius: 2, borderStyle: 'dashed' }}>
            <CardContent sx={{ py: 5, textAlign: 'center' }}>
              <Typography color="text.secondary">No public job postings right now. Check back soon.</Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <TextField
                size="small"
                label="Search"
                placeholder="Title, skills, description, job code…"
                value={jobFilters.search}
                onChange={(e) => setJobFilters((f) => ({ ...f, search: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  )
                }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  size="small"
                  label="Location"
                  placeholder="City, state, country…"
                  value={jobFilters.location}
                  onChange={(e) => setJobFilters((f) => ({ ...f, location: e.target.value }))}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Client / company"
                  value={jobFilters.client}
                  onChange={(e) => setJobFilters((f) => ({ ...f, client: e.target.value }))}
                  fullWidth
                />
                <FormControl size="small" fullWidth sx={{ minWidth: 160 }}>
                  <InputLabel>Work arrangement</InputLabel>
                  <Select
                    label="Work arrangement"
                    value={jobFilters.remote}
                    onChange={(e) => setJobFilters((f) => ({ ...f, remote: e.target.value }))}
                  >
                    <MenuItem value="any">Any</MenuItem>
                    <MenuItem value="remote">Remote</MenuItem>
                    <MenuItem value="hybrid">Hybrid</MenuItem>
                    <MenuItem value="onsite">On-site</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Showing {filteredJobs.length} of {jobs.length} roles
              </Typography>
            </Stack>
            {filteredJobs.length === 0 ? (
              <Typography color="text.secondary">No roles match your filters. Try adjusting search or filters.</Typography>
            ) : (
              <Grid container spacing={2}>
                {filteredJobs.map((job) => (
                  <Grid item xs={12} sm={6} md={4} key={job.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderRadius: 2,
                        transition: 'box-shadow 0.2s, transform 0.2s',
                        ...panelCardSx,
                        '&:hover': {
                          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {job.job_code}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, mb: 1, lineHeight: 1.3 }}>
                          {job.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {job.client?.name || job.client?.company_name || '—'} · {job.location || job.city || '—'}
                        </Typography>
                        <Button component={RouterLink} to={`/query/jobs/${job.id}`} variant="contained" size="small" fullWidth>
                          View role
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Box>

      <Box id="portal-training" sx={{ scrollMarginTop: 88, mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <SchoolIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography
            component={RouterLink}
            to={sectionHref('portal-training')}
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Trainings
          </Typography>
        </Stack>
        {!trainingPublic ? (
          <Typography color="text.secondary">Training catalog is not shown on the public page.</Typography>
        ) : trainingRows.length === 0 ? (
          <Typography color="text.secondary">No published programs to highlight yet.</Typography>
        ) : (
          <Grid container spacing={2}>
            {trainingRows.map((cat) => (
              <Grid item xs={12} key={cat.id}>
                <Card
                  id={`portal-training-cat-${cat.id}`}
                  variant="outlined"
                  sx={{ borderRadius: 2, scrollMarginTop: { xs: 72, sm: 88 }, ...panelCardSx }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {cat.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
                      {cat.description || '—'}
                    </Typography>
                    <Chip label={`${cat.publishedCourseCount || 0} courses`} size="small" sx={{ mb: 2 }} />
                    {groupCoursesByLearningSpot(cat.publishedCourses).map((spot) => (
                      <Box key={`${cat.id}-${spot.id || spot.name}`} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          {spot.name}
                        </Typography>
                        <Stack spacing={1} alignItems="stretch">
                          {spot.courses.map((c) => (
                            <Button
                              key={c.id}
                              variant="outlined"
                              size="small"
                              onClick={() => setTrainingDialogCourseId(c.id)}
                              sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.25 }}
                            >
                              {c.name}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {formatDeliveryType(c.delivery_type)}
                              </Typography>
                            </Button>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Box id="portal-exams" sx={{ scrollMarginTop: 88, mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <QuizIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography
            component={RouterLink}
            to={sectionHref('portal-exams')}
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Exams
          </Typography>
        </Stack>
        {!examsPublic ? (
          <Typography color="text.secondary">Exams are not listed publicly for this organization.</Typography>
        ) : exams.length === 0 ? (
          <Typography color="text.secondary">No standalone exams published for public listing.</Typography>
        ) : (
          <Grid container spacing={2}>
            {exams.map((ex) => (
              <Grid item xs={12} sm={6} md={4} key={ex.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', ...panelCardSx }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {ex.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {ex.description
                        ? ex.description.length > 160
                          ? `${ex.description.slice(0, 160)}…`
                          : ex.description
                        : '—'}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                      {ex.time_limit_minutes != null && <Chip size="small" label={`${ex.time_limit_minutes} min`} />}
                      {ex.pass_score_percent != null && (
                        <Chip size="small" variant="outlined" label={`Pass ${ex.pass_score_percent}%`} />
                      )}
                    </Stack>
                    <Button variant="contained" size="small" fullWidth onClick={() => setExamDialogId(ex.id)}>
                      View details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Box id="portal-benches" sx={{ scrollMarginTop: 88, mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <GroupWorkIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography
            component={RouterLink}
            to={sectionHref('portal-benches')}
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Benches
          </Typography>
        </Stack>
        {!benchesPublic ? (
          <Typography color="text.secondary">Bench marketing is not listed publicly for this organization.</Typography>
        ) : publicBenches.length === 0 ? (
          <Typography color="text.secondary">
            No benches are marked Public yet, or none are operational.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {publicBenches.map((b) => (
              <Grid item xs={12} md={6} key={b.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', ...panelCardSx }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {b.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {b.bench_code || '—'}
                    </Typography>
                    {b.description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {b.description.length > 200 ? `${b.description.slice(0, 200)}…` : b.description}
                      </Typography>
                    ) : null}
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Candidates ({(b.candidates || []).length})
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      {(b.candidates || []).slice(0, 24).map((c) => (
                        <Chip key={c.id || c.name} size="small" label={c.name || '—'} variant="outlined" />
                      ))}
                      {(b.candidates || []).length > 24 ? (
                        <Chip size="small" label={`+${(b.candidates || []).length - 24} more`} />
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Box id="portal-more" sx={{ scrollMarginTop: 88 }}>
        <Card
          sx={{
            borderRadius: 2,
            ...panelCardSx,
            background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(
              theme.palette.secondary.main,
              0.05
            )}), ${contentPanelTheme.panelBg}`,
            border: '1px solid',
            borderColor: contentPanelTheme.panelBorder
          }}
        >
          <CardContent sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              More coming
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
              Additional public resources will appear here as your organization enables them.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  const splitTrainingCategory =
    trainingBrowseScope === 'category' ? trainingRows.find((c) => c.id === selectedTrainingId) : null;
  const splitTrainingGroups = splitTrainingCategory
    ? groupCoursesByLearningSpot(splitTrainingCategory.publishedCourses)
    : [];

  const browsePanelScrollable = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          gap: 1,
          flexShrink: 0
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
          Browse
        </Typography>
        <Tooltip title="Hide list — wider preview">
          <IconButton size="small" onClick={hideSplitList} aria-label="Hide browse list">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <ToggleButtonGroup
            value={splitTab}
            exclusive
            fullWidth
            orientation={isMdUp ? 'vertical' : 'horizontal'}
            size="small"
            sx={{ mb: 2, alignItems: 'stretch', '& .MuiToggleButton-root': { justifyContent: 'flex-start', py: 1 } }}
            onChange={(e, v) => v != null && onSplitTabChange(v)}
          >
            {jobsPublic && (
              <ToggleButton value="jobs">
                <WorkIcon sx={{ mr: 1, fontSize: 20 }} />
                Jobs ({jobs.length})
              </ToggleButton>
            )}
            {trainingPublic && (
              <ToggleButton value="training">
                <SchoolIcon sx={{ mr: 1, fontSize: 20 }} />
                Trainings ({trainingRows.length})
              </ToggleButton>
            )}
            {examsPublic && (
              <ToggleButton value="exams">
                <QuizIcon sx={{ mr: 1, fontSize: 20 }} />
                Exams ({exams.length})
              </ToggleButton>
            )}
            <ToggleButton value="more">
              <AutoAwesomeIcon sx={{ mr: 1, fontSize: 20 }} />
              More
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ my: 1 }} />

          {splitTab === 'jobs' && jobsPublic && (
            <List dense disablePadding>
              <ListSubheader component="div" sx={{ lineHeight: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => {
                    setJobSplitView('browse');
                    setDetailJob(null);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    border: 0,
                    background: 'none',
                    p: 0,
                    font: 'inherit',
                    color: 'primary.main',
                    textAlign: 'left'
                  }}
                >
                  Open positions
                </Link>
              </ListSubheader>
              {filteredJobs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  {jobs.length === 0 ? 'No listings.' : 'No listings match filters — adjust filters on the right.'}
                </Typography>
              ) : (
                filteredJobs.map((job) => (
                  <ListItemButton
                    key={job.id}
                    selected={selectedJobId === job.id}
                    onClick={() => openJobDetailInFrame(job.id)}
                    alignItems="flex-start"
                  >
                    <ListItemText
                      primary={job.title}
                      secondary={`${job.job_code} · ${job.location || job.city || '—'}`}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          )}

          {splitTab === 'training' && trainingPublic && (
            <List dense disablePadding>
              <ListSubheader component="div" sx={{ lineHeight: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => {
                    setTrainingBrowseScope('all');
                    setSelectedTrainingId(null);
                    setTrainingViewMode('category');
                    setSelectedTrainingCourseId(null);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    border: 0,
                    background: 'none',
                    p: 0,
                    font: 'inherit',
                    color: 'primary.main',
                    textAlign: 'left'
                  }}
                >
                  Trainings
                </Link>
              </ListSubheader>
              {trainingRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  None published.
                </Typography>
              ) : (
                <>
                  <ListItemButton
                    selected={trainingBrowseScope === 'all'}
                    onClick={() => {
                      setTrainingBrowseScope('all');
                      setSelectedTrainingId(null);
                      setTrainingViewMode('category');
                      setSelectedTrainingCourseId(null);
                    }}
                  >
                    <ListItemText primary="All trainings" secondary="Search & filter every course" />
                  </ListItemButton>
                  <Divider sx={{ my: 0.5 }} />
                  {trainingRows.map((cat) => (
                    <ListItemButton
                      key={cat.id}
                      selected={trainingBrowseScope === 'category' && selectedTrainingId === cat.id}
                      onClick={() => {
                        setTrainingBrowseScope('category');
                        setSelectedTrainingId(cat.id);
                        setTrainingViewMode('category');
                        setSelectedTrainingCourseId(null);
                      }}
                    >
                      <ListItemText primary={cat.name} secondary={`${cat.publishedCourseCount || 0} courses`} />
                    </ListItemButton>
                  ))}
                </>
              )}
            </List>
          )}

          {splitTab === 'exams' && examsPublic && (
            <List dense disablePadding>
              <ListSubheader component="div" sx={{ lineHeight: 2 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => {
                    setExamFilters({ search: '', timeLimit: 'any', passAtLeast: '' });
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    border: 0,
                    background: 'none',
                    p: 0,
                    font: 'inherit',
                    color: 'primary.main',
                    textAlign: 'left'
                  }}
                >
                  Exams
                </Link>
              </ListSubheader>
              {exams.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  None listed.
                </Typography>
              ) : filteredExams.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  No exams match filters — adjust filters on the right.
                </Typography>
              ) : (
                filteredExams.map((ex) => (
                  <ListItemButton key={ex.id} selected={selectedExamId === ex.id} onClick={() => setSelectedExamId(ex.id)}>
                    <ListItemText primary={ex.name} secondary={ex.description ? ex.description.slice(0, 80) : '—'} />
                  </ListItemButton>
                ))
              )}
            </List>
          )}

      {splitTab === 'more' && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
          Future modules will appear in this area.
        </Typography>
      )}
    </>
  );

  const previewPaper = (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        m: { xs: 0, md: 2 },
        mb: { xs: 2, md: 2 },
        p: 3,
        minHeight: { xs: 240, md: 0 },
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        ...splitPreviewPanelCardSx,
        ...splitPreviewPanelTextScopeSx
      }}
    >
      {splitListHidden && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexShrink: 0 }}>
          <Tooltip title="Show browse list">
            <IconButton size="small" onClick={showSplitList} aria-label="Show browse list">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            Browse list hidden — maximum space for preview
          </Typography>
        </Stack>
      )}
      {splitTab === 'jobs' && !jobsPublic ? (
        <Typography color="text.secondary">Job listings are not available on the public portal.</Typography>
      ) : splitTab === 'jobs' && jobsPublic ? (
        jobSplitView === 'detail' ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setJobSplitView('browse');
                setDetailJob(null);
              }}
              sx={{ mb: 2 }}
            >
              Back to listings
            </Button>
            {detailLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : detailJob ? (
              <PublicJobDetailBody job={detailJob} compact />
            ) : (
              <Alert severity="warning">Could not load this role.</Alert>
            )}
          </Box>
        ) : (
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              Find a role
            </Typography>
            <TextField
              size="small"
              label="Search"
              placeholder="Title, skills, description, job code…"
              value={jobFilters.search}
              onChange={(e) => setJobFilters((f) => ({ ...f, search: e.target.value }))}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              size="small"
              label="Location"
              placeholder="City, state, country…"
              value={jobFilters.location}
              onChange={(e) => setJobFilters((f) => ({ ...f, location: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="Client / company"
              value={jobFilters.client}
              onChange={(e) => setJobFilters((f) => ({ ...f, client: e.target.value }))}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Work arrangement</InputLabel>
              <Select
                label="Work arrangement"
                value={jobFilters.remote}
                onChange={(e) => setJobFilters((f) => ({ ...f, remote: e.target.value }))}
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="remote">Remote</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
                <MenuItem value="onsite">On-site</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Showing {filteredJobs.length} of {jobs.length} roles
            </Typography>
            <List
              dense
              sx={{
                flex: 1,
                overflow: 'auto',
                minHeight: 120,
                maxHeight: { md: 'calc(100dvh - 320px)' },
                border: 1,
                borderColor: splitPreviewPanelTheme.panelBorder,
                borderRadius: 1
              }}
            >
              {filteredJobs.length === 0 ? (
                <ListItemText
                  sx={{ px: 2, py: 1 }}
                  primary={jobs.length === 0 ? 'No public roles yet.' : 'No roles match — try different filters.'}
                />
              ) : (
                filteredJobs.map((job) => (
                  <ListItemButton
                    key={job.id}
                    selected={selectedJobId === job.id}
                    onClick={() => openJobDetailInFrame(job.id)}
                    alignItems="flex-start"
                  >
                    <ListItemText
                      primary={job.title}
                      secondary={`${job.job_code} · ${job.client?.name || job.client?.company_name || '—'} · ${job.location || job.city || '—'}`}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Tip: click a role for the full description here — no new tab.
            </Typography>
          </Stack>
        )
      ) : splitTab === 'training' && trainingPublic ? (
        trainingViewMode === 'course' && selectedTrainingCourseId ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setTrainingViewMode('category');
                setSelectedTrainingCourseId(null);
              }}
              sx={{ mb: 2 }}
            >
              Back to courses
            </Button>
            {detailTrainingLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : detailTrainingCourse ? (
              <PublicTrainingCourseDetailBlock
                course={detailTrainingCourse}
                summary={trainingFeedbackSummary}
                feedbackEligibility={feedbackEligibility}
                feedbackRating={feedbackRating}
                onFeedbackRatingChange={setFeedbackRating}
                feedbackThumb={feedbackThumb}
                onFeedbackThumbChange={setFeedbackThumb}
                feedbackComment={feedbackComment}
                onFeedbackCommentChange={setFeedbackComment}
                onSubmitFeedback={submitSplitTrainingFeedback}
                feedbackSubmitting={feedbackSubmitting}
                feedbackMessage={feedbackMessage}
              />
            ) : (
              <Alert severity="warning">Could not load this course.</Alert>
            )}
          </Box>
        ) : trainingBrowseScope === 'all' ? (
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              All training programs
            </Typography>
            <TextField
              size="small"
              label="Search"
              placeholder="Course, category, learning area…"
              value={trainingFilters.search}
              onChange={(e) => setTrainingFilters((f) => ({ ...f, search: e.target.value }))}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                )
              }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Program</InputLabel>
                <Select
                  label="Program"
                  value={trainingFilters.categoryId}
                  onChange={(e) => setTrainingFilters((f) => ({ ...f, categoryId: e.target.value }))}
                >
                  <MenuItem value="">All programs</MenuItem>
                  {trainingRows.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Delivery</InputLabel>
                <Select
                  label="Delivery"
                  value={trainingFilters.delivery}
                  onChange={(e) => setTrainingFilters((f) => ({ ...f, delivery: e.target.value }))}
                >
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="SPT">Self-paced (SPT)</MenuItem>
                  <MenuItem value="ILT">Instructor-led (ILT)</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            {learningSpotOptions.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel>Learning area</InputLabel>
                <Select
                  label="Learning area"
                  value={trainingFilters.learningSpotId}
                  onChange={(e) => setTrainingFilters((f) => ({ ...f, learningSpotId: e.target.value }))}
                >
                  <MenuItem value="">All areas</MenuItem>
                  {learningSpotOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Typography variant="caption" color="text.secondary">
              Showing {filteredAllTrainingCourses.length} of {allTrainingCoursesFlat.length} courses
            </Typography>
            <List
              dense
              sx={{
                flex: 1,
                overflow: 'auto',
                minHeight: 120,
                maxHeight: { md: 'calc(100dvh - 380px)' },
                border: 1,
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              {filteredAllTrainingCourses.length === 0 ? (
                <ListItemText sx={{ px: 2, py: 1 }} primary="No courses match — try different filters." />
              ) : (
                filteredAllTrainingCourses.map((c) => (
                  <ListItemButton
                    key={c.id}
                    onClick={() => {
                      setSelectedTrainingCourseId(c.id);
                      setTrainingViewMode('course');
                    }}
                    alignItems="flex-start"
                  >
                    <ListItemText
                      primary={c.name}
                      secondary={`${c.categoryName} · ${c.subCategoryName || '—'} · ${formatDeliveryType(c.delivery_type)}`}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
            <Typography variant="caption" color="text.secondary">
              Open a course for full details, enrollment, and feedback (after you complete the course).
            </Typography>
          </Stack>
        ) : !splitTrainingCategory ? (
          <Typography color="text.secondary">Select a program on the left, or choose All programs.</Typography>
        ) : (
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              {splitTrainingCategory.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {splitTrainingCategory.description || '—'}
            </Typography>
            <Chip
              label={`${splitTrainingCategory.publishedCourseCount || 0} published courses`}
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            />
            <Divider />
            {splitTrainingGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No courses in this program.
              </Typography>
            ) : (
              splitTrainingGroups.map((spot) => (
                <Box key={`${splitTrainingCategory.id}-${spot.id || spot.name}`}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {spot.name}
                  </Typography>
                  <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {spot.courses.map((c) => (
                      <ListItemButton
                        key={c.id}
                        onClick={() => {
                          setSelectedTrainingCourseId(c.id);
                          setTrainingViewMode('course');
                        }}
                        alignItems="flex-start"
                      >
                        <ListItemText
                          primary={c.name}
                          secondary={formatDeliveryType(c.delivery_type)}
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              ))
            )}
            <Typography variant="caption" color="text.secondary">
              Open a course for schedule details, ratings, and enrollment.
            </Typography>
          </Stack>
        )
      ) : splitTab === 'exams' && examsPublic ? (
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary">
            Exams
          </Typography>
          <TextField
            size="small"
            label="Search"
            placeholder="Exam name or description…"
            value={examFilters.search}
            onChange={(e) => setExamFilters((f) => ({ ...f, search: e.target.value }))}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              )
            }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Duration</InputLabel>
              <Select
                label="Duration"
                value={examFilters.timeLimit}
                onChange={(e) => setExamFilters((f) => ({ ...f, timeLimit: e.target.value }))}
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="short">Up to 45 minutes</MenuItem>
                <MenuItem value="long">Over 45 minutes</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Min pass %"
              type="number"
              placeholder="e.g. 70"
              value={examFilters.passAtLeast}
              onChange={(e) => setExamFilters((f) => ({ ...f, passAtLeast: e.target.value }))}
              fullWidth
              inputProps={{ min: 0, max: 100 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredExams.length} of {exams.length} exams
          </Typography>
          <Divider />
          {detailExamLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailExam ? (
            <PublicExamDetailBlock exam={detailExam} />
          ) : (
            <Alert severity="warning">Could not load this exam.</Alert>
          )}
        </Stack>
      ) : (
        <>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
            Preview
          </Typography>
          <SplitMorePreviewPanel splitTab={splitTab} />
        </>
      )}
    </Paper>
  );

  const splitFrameMobile = (
    <Grid
      container
      spacing={0}
      sx={{
        flex: 1,
        minHeight: { xs: 'auto', md: 'calc(100dvh - 64px)' },
        alignItems: 'stretch',
        width: '100%'
      }}
    >
      {!splitListHidden && (
        <Grid
          item
          xs={12}
          sx={{
            borderBottom: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            ...splitBrowsePanelSurfaceSx
          }}
        >
          <Box
            sx={{
              p: 2,
              flex: 1,
              overflow: 'auto',
              minHeight: { xs: 200, md: 0 },
              ...splitBrowsePanelTextScopeSx
            }}
          >
            {browsePanelScrollable}
          </Box>
        </Grid>
      )}
      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', minHeight: { xs: 280, md: 0 } }}>
        {previewPaper}
      </Grid>
    </Grid>
  );

  const splitFrameDesktop = (
    <Box
      ref={splitContainerRef}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        minHeight: { md: 'calc(100dvh - 64px)' },
        width: '100%',
        minWidth: 0
      }}
    >
      {!splitListHidden && (
        <>
          <Box
            sx={{
              flex: `0 0 ${splitLeftPct}%`,
              width: `${splitLeftPct}%`,
              minWidth: 200,
              maxWidth: '48%',
              borderRight: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              ...splitBrowsePanelSurfaceSx
            }}
          >
            <Box sx={{ p: 2, flex: 1, overflow: 'auto', minHeight: 0, ...splitBrowsePanelTextScopeSx }}>
              {browsePanelScrollable}
            </Box>
          </Box>
          <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panels"
            onMouseDown={handleSplitDividerPointerDown}
            onTouchStart={handleSplitDividerPointerDown}
            sx={{
              flex: '0 0 10px',
              width: 10,
              ml: '-2px',
              mr: '-2px',
              cursor: 'col-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'action.selected' }
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 56,
                borderRadius: 1,
                bgcolor: 'divider',
                pointerEvents: 'none',
                boxShadow: (t) => `inset 0 0 0 1px ${t.palette.divider}`
              }}
            />
          </Box>
        </>
      )}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{previewPaper}</Box>
    </Box>
  );

  const splitFrame = isMdUp ? splitFrameDesktop : splitFrameMobile;

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {layoutMode === 'regular' ? (
        <Container maxWidth="lg" sx={{ py: 2, flex: 1 }}>
          <Box
            sx={{
              mb: 3,
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.22),
              background: `linear-gradient(125deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(
                theme.palette.secondary.main,
                0.08
              )} 50%, ${alpha(theme.palette.primary.light, 0.06)} 100%)`,
              boxShadow: `0 20px 48px ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -48,
                right: -40,
                width: 200,
                height: 200,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.secondary.main, 0.12),
                pointerEvents: 'none'
              }}
            />
            <Box sx={{ position: 'relative' }}>
              <Typography variant="overline" sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: 0.14 }}>
                ERP for Consulting
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, mb: 1, letterSpacing: '-0.02em' }}>
                {publicBranding.companyName?.trim() || 'Careers & learning'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640, lineHeight: 1.65 }}>
                {publicBranding.companyTagline?.trim() ||
                  'Unify sales, delivery, HR, and compliance in one ERP built for professional services firms.'}
              </Typography>
            </Box>
          </Box>
          {alertsBlock}
          {anyPublic && (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {jobsPublic && (
                <Button component={RouterLink} to={sectionHref('portal-jobs')} size="small" variant="text">
                  Jobs
                </Button>
              )}
              {trainingPublic && (
                <Button component={RouterLink} to={sectionHref('portal-training')} size="small" variant="text">
                  Training
                </Button>
              )}
              {examsPublic && (
                <Button component={RouterLink} to={sectionHref('portal-exams')} size="small" variant="text">
                  Exams
                </Button>
              )}
            </Stack>
          )}
          {regularSections}
        </Container>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            px: { xs: 1, sm: 2 },
            pt: 1,
            pb: 2
          }}
        >
          <Box sx={{ px: 0.5, pb: 1.5 }}>{alertsBlock}</Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: { xs: 2, md: 3 },
              overflow: 'hidden',
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.2),
              boxShadow: `0 24px 56px ${alpha(theme.palette.common.black, 0.12)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.35)
            }}
          >
            <Box
              sx={{
                height: 5,
                flexShrink: 0,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              }}
            />
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{splitFrame}</Box>
          </Box>
        </Box>
      )}
      <Dialog
        open={Boolean(trainingDialogCourseId)}
        onClose={() => setTrainingDialogCourseId(null)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>Course details</DialogTitle>
        <DialogContent dividers>
          {dialogTrainingLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : dialogTrainingCourse ? (
            <PublicTrainingCourseDetailBlock
              course={dialogTrainingCourse}
              summary={dialogTrainingFeedbackSummary}
              feedbackEligibility={dialogFeedbackEligibility}
              feedbackRating={dialogFeedbackRating}
              onFeedbackRatingChange={setDialogFeedbackRating}
              feedbackThumb={dialogFeedbackThumb}
              onFeedbackThumbChange={setDialogFeedbackThumb}
              feedbackComment={dialogFeedbackComment}
              onFeedbackCommentChange={setDialogFeedbackComment}
              onSubmitFeedback={submitDialogTrainingFeedback}
              feedbackSubmitting={dialogFeedbackSubmitting}
              feedbackMessage={dialogFeedbackMessage}
            />
          ) : (
            <Alert severity="warning">Could not load this course.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrainingDialogCourseId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(examDialogId)}
        onClose={() => setExamDialogId(null)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>Exam details</DialogTitle>
        <DialogContent dividers>
          {dialogExamLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : dialogExam ? (
            <PublicExamDetailBlock exam={dialogExam} />
          ) : (
            <Alert severity="warning">Could not load this exam.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExamDialogId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicPortalPage;
